<template>
  <div class="orders-revenue-section" v-loading="isLoading">
    <h3 class="section-title">Order Volume & Cancellation Trend</h3>
    
    <!-- Info Box 1 -->
    <div v-if="showInfoBox1" class="info-box">
      <div class="info-box-content">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 0C4.032 0 0 4.032 0 9C0 13.968 4.032 18 9 18C13.968 18 18 13.968 18 9C18 4.032 13.968 0 9 0ZM9.9 13.5H8.1V8.1H9.9V13.5ZM9.9 6.3H8.1V4.5H9.9V6.3Z" fill="#3498DB"/>
          </svg>
        </div>
        <div v-if = "dashboardFilters.viewRole === 'Admin'" class="info-text">
          <p class="info-title">Practice View Movement Insight</p>
          <p class="info-message">
            Shows created and canceled orders across the practice. Created orders appear as positive bars and canceled orders appear as negative values. The line shows net order volume (created - canceled).
          </p>
        </div>
        <div v-else class="info-text">
          <p class="info-title">Provider View Movement Insight</p>
          <p class="info-message">
            Shows created and canceled orders for me. Created orders appear as positive bars and canceled orders appear as negative values. The line shows net order volume (created - canceled).
          </p>
        </div>
      </div>
      <button class="info-close" @click="showInfoBox1 = false">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M11.25 1.8075L10.1925 0.75L6 4.9425L1.8075 0.75L0.75 1.8075L4.9425 6L0.75 10.1925L1.8075 11.25L6 7.0575L10.1925 11.25L11.25 10.1925L7.0575 6L11.25 1.8075Z" fill="#004879"/>
        </svg>
      </button>
    </div>

    <!-- Info Box 2 -->
    <div v-if="showInfoBox2" class="info-box">
      <div class="info-box-content">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 0C4.032 0 0 4.032 0 9C0 13.968 4.032 18 9 18C13.968 18 18 13.968 18 9C18 4.032 13.968 0 9 0ZM9.9 13.5H8.1V8.1H9.9V13.5ZM9.9 6.3H8.1V4.5H9.9V6.3Z" fill="#3498DB"/>
          </svg>
        </div>
        <div class="info-text">
          <p class="info-title">Order Movement Summary</p>
          <p class="info-message">
            Across the selected period, orders created totaled ${{ store.state.myPractice.clinicDashboard.orderRevenueChartData?.total_created_orders_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0 }} and cancellations totaled ${{ store.state.myPractice.clinicDashboard.orderRevenueChartData?.total_canceled_orders_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0 }}, resulting in ${{ store.state.myPractice.clinicDashboard.orderRevenueChartData?.total_net_order_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0 }} net order volume.
          </p>
        </div>
      </div>  
      <button class="info-close" @click="showInfoBox2 = false">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M11.25 1.8075L10.1925 0.75L6 4.9425L1.8075 0.75L0.75 1.8075L4.9425 6L0.75 10.1925L1.8075 11.25L6 7.0575L10.1925 11.25L11.25 10.1925L7.0575 6L11.25 1.8075Z" fill="#004879"/>
        </svg>
      </button>
    </div>

    <!-- Chart Card -->
    <div class="chart-card">
      <div class="chart-wrapper">
        <canvas ref="chartCanvas"></canvas>
        <div ref="tooltipEl" class="custom-tooltip"></div>
        <div v-if="!hasData" class="chart-empty">No order/revenue data available.</div>
      </div>
      
      <!-- Legend Row 1 -->
      <div class="legend-row">
        <div class="legend-item">
          <div class="legend-circle" style="background: #81B0E1"></div>
          <span>Lab Markup</span>
        </div>
        <div class="legend-item">
          <div class="legend-circle" style="background: #5587BC"></div>
          <span>Lab Patient Pay</span>
        </div>
        <div class="legend-item">
          <div class="legend-circle" style="background: #004879"></div>
          <span>Lab Provider Pay</span>
        </div>
        <div class="legend-item">
          <div class="legend-circle" style="background: #8BBDC7"></div>
          <span>Practice Products</span>
        </div>
        <div class="legend-item">
          <div class="legend-circle" style="background: #25B8C2"></div>
          <span>Service Fee</span>
        </div>
      </div>

      <!-- Legend Row 2 -->
      <div class="legend-row">
        <!-- <div class="legend-item">
          <div class="legend-circle" style="background: #20A7C1"></div>
          <span>Subscriptions</span>
        </div> -->
        <div class="legend-item">
          <div class="legend-circle" style="background: #979797"></div>
          <span>Canceled</span>
        </div>
        <div class="legend-item">
          <div class="legend-circle" style="background: #fff; border: 3px solid #25B8C2"></div>
          <span>Volume Trend</span>
        </div>
      </div>

      <!-- Disclaimer -->
      <p class="chart-disclaimer">
        * Order-level view. Created volume does not retroactively change when cancellations occur; cancellation appear in the period they happen.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Chart, registerables } from 'chart.js';
import { processOrderRevenueData, REVENUE_FIELD_ORDER, REVENUE_FIELD_LABELS, CHART_COLORS } from '../utils/chartHelpers';

Chart.register(...registerables);

const store = useStore();
const chartCanvas = ref(null);
const tooltipEl = ref(null);
let chartInstance = null;

const showInfoBox1 = ref(true);
const showInfoBox2 = ref(true);

const isLoading = computed(() => store.state.myPractice.clinicDashboard.orderRevenueChartLoading);
const buckets = computed(() => store.getters['myPractice/clinicDashboard/orderRevenueChartBucketsData']);
const hasData = computed(() => buckets.value && buckets.value.length > 0);
const dashboardFilters = computed(() => store.state.myPractice.clinicDashboard.dashboardFilters);

// Custom external tooltip handler
const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const tooltipElement = tooltipEl.value;
  
  if (!tooltipElement) return;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipElement.style.opacity = '0';
    return;
  }

  // Set content
  if (tooltip.body) {
    const dataIndex = tooltip.dataPoints[0].dataIndex;
    const bucket = buckets.value[dataIndex];
    
    if (!bucket) return;

    // Calculate totals
    const createdTotal = bucket.created_orders_amount;
    
    const canceledTotal = bucket.canceled_orders_amount;

    const clinicRevenueCreated = (
      (bucket.created_clinic_revenue_lab_markup || 0) +
      (bucket.created_clinic_revenue_practice_products || 0) +
      (bucket.created_clinic_revenue_consultation_fee || 0)
    );

    const labRevenueCreated = (
      (bucket.created_payments_to_vibrant_lab_patient_pay || 0) +
      (bucket.created_payments_to_vibrant_lab_provider_pay || 0)
    );

    const netRevenue = bucket.net_order_amount || 0;
    const summary = createdTotal;

    let innerHtml = '<div class="tooltip-content">';
    
    // Title
    innerHtml += `<p class="tooltip-title">${tooltip.title[0]}</p>`;
    
    // Summary
    innerHtml += '<div class="tooltip-row">';
    innerHtml += '<span class="tooltip-label-bold">Summary:</span>';
    innerHtml += `<span class="tooltip-value">$ ${bucket.created_orders_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    // Clinic Revenue Section
    innerHtml += '<div class="tooltip-section">';
    innerHtml += '<div class="tooltip-row">';
    innerHtml += '<span class="tooltip-label-bold tooltip-clinic">Clinic Revenue</span>';
    innerHtml += `<span class="tooltip-value tooltip-clinic">$ ${clinicRevenueCreated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    // Clinic Revenue Items
    innerHtml += '<div class="tooltip-row tooltip-item">';
    innerHtml += '<span class="tooltip-dot" style="background: #9AC0E7;"></span>';
    innerHtml += '<span class="tooltip-label">Lab Markup:</span>';
    innerHtml += `<span class="tooltip-value">$ ${(bucket.created_clinic_revenue_lab_markup || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    innerHtml += '<div class="tooltip-row tooltip-item">';
    innerHtml += '<span class="tooltip-dot" style="background: #A2CAD2;"></span>';
    innerHtml += '<span class="tooltip-label">Practice Products:</span>';
    innerHtml += `<span class="tooltip-value">$ ${(bucket.created_clinic_revenue_practice_products || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    innerHtml += '<div class="tooltip-row tooltip-item">';
    innerHtml += '<span class="tooltip-dot" style="background: #51C6CE;"></span>';
    innerHtml += '<span class="tooltip-label">Service Fee:</span>';
    innerHtml += `<span class="tooltip-value">$ ${(bucket.created_clinic_revenue_consultation_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';
    innerHtml += '</div>';

    // Lab Revenue Section
    innerHtml += '<div class="tooltip-section">';
    innerHtml += '<div class="tooltip-row">';
    innerHtml += '<span class="tooltip-label-bold tooltip-lab">Payments to Vibrant</span>';
    innerHtml += `<span class="tooltip-value tooltip-lab">$ ${labRevenueCreated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    innerHtml += '<div class="tooltip-row tooltip-item">';
    innerHtml += '<span class="tooltip-dot" style="background: #779FC9;"></span>';
    innerHtml += '<span class="tooltip-label">Lab Patient Pay:</span>';
    innerHtml += `<span class="tooltip-value">$ ${(bucket.created_payments_to_vibrant_lab_patient_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    innerHtml += '<div class="tooltip-row tooltip-item">';
    innerHtml += '<span class="tooltip-dot" style="background: #336D94;"></span>';
    innerHtml += '<span class="tooltip-label">Lab Provider Pay</span>';
    innerHtml += `<span class="tooltip-value">$ ${(bucket.created_payments_to_vibrant_lab_provider_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';
    innerHtml += '</div>';

    // Canceled
    innerHtml += '<div class="tooltip-row">';
    innerHtml += '<span class="tooltip-label-bold">Canceled:</span>';
    innerHtml += `<span class="tooltip-value">$ ${canceledTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    // Net Revenue
    innerHtml += '<div class="tooltip-row">';
    innerHtml += '<span class="tooltip-label-bold">Net Revenue:</span>';
    innerHtml += `<span class="tooltip-value">$ ${netRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
    innerHtml += '</div>';

    innerHtml += '</div>';
    tooltipElement.innerHTML = innerHtml;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Position tooltip above the point with spacing
  tooltipElement.style.opacity = '1';
  tooltipElement.style.left = positionX + tooltip.caretX + 'px';
  tooltipElement.style.top = positionY + tooltip.caretY + 'px';
  tooltipElement.style.transform = 'translate(-50%, calc(-100% - 10px))';
};

function renderChart() {
  if (!chartCanvas.value || !hasData.value) return;
  if (chartInstance) chartInstance.destroy();

  const { labels, created, canceled, net, yAxisConfig } = processOrderRevenueData(buckets.value);

  const datasets = [];

  const pairCount = REVENUE_FIELD_ORDER.created.length;
  const sideInset = 8; // background 左右各少 8px
  const gap = 4;
  const backgroundRatio = 0.9;

  function getUnitWidth(chart) {
    const xScale = chart?.scales?.x;
    if (!xScale || xScale.ticks.length < 2) return 100;
    return xScale.getPixelForTick(1) - xScale.getPixelForTick(0);
  }

  function getLayout(chart) {
    const unitWidth = getUnitWidth(chart);
    const backgroundWidth = unitWidth * backgroundRatio;
    const innerSpan = backgroundWidth - sideInset * 2;

    // 为了让 bars 最终占用 innerSpan，并且左右视觉上各留 half-gap
    const groupWidth = innerSpan + gap;

    const categoryPercentage = groupWidth / unitWidth;
    const barPercentage =
      (innerSpan - gap * (pairCount - 1)) / groupWidth;

    return {
      categoryPercentage,
      barPercentage,
      backgroundWidth,
    };
  }

  const customXAxisLabelsPlugin = {
    id: 'customXAxisLabels',

    afterDraw(chart) {
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;

      if (!xScale || !yScale) return;

      // 让 label 出现在最小负值位置再往下 5px
      const stepSize = yScale.options.ticks.stepSize;
      const labelY = yScale.min === 0 ? yScale.getPixelForValue(yScale.min) + 11 : yScale.getPixelForValue(yScale.min + Math.ceil(stepSize / 2));

      // 竖线 tick 的顶部和底部
      const tickTopY = yScale.getPixelForValue(0);   // 从 0 轴开始
      const tickBottomY = labelY - 8;                // 到 label 上方一点

      ctx.save();

      // 画竖线 tick
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 1;

      // 画 label
      ctx.fillStyle = '#666';
      ctx.font = '12px Inter 400';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      xScale.ticks.forEach((tick, i) => {
        const x = xScale.getPixelForTick(i);

        // 竖线 tick
        ctx.beginPath();
        ctx.moveTo(x, tickTopY);
        ctx.lineTo(x, tickBottomY);
        ctx.stroke();

        // label
        ctx.fillText(String(tick.label), x, labelY);
      });

      ctx.restore();
    }
  };

  // Background
  datasets.push({
    label: 'Net Revenue Background',
    type: 'bar',
    data: net,
    backgroundColor: (ctx) => {
      const chart = ctx.chart;
      const active = chart.getActiveElements();
      if (active.length > 0) {
        const hoverIndex = active[0].index;

        if (ctx.dataIndex === hoverIndex) {
          return '#F0F7FF'; // 👈 hover 高亮
        }
      }

      return '#E4F3F5'; // 默认
    },
    hoverBackgroundColor: (ctx) => {
      const chart = ctx.chart;
      const active = chart.getActiveElements();
      if (active.length > 0) {
        const hoverIndex = active[0].index;

        if (ctx.dataIndex === hoverIndex) {
          return '#F0F7FF'; // 👈 hover 高亮
        }
      }

      return '#E4F3F5'; // 默认
    },
    grouped: false,
    borderRadius: (ctx) => {
      const value = ctx.raw; // 当前 bar 的值

      if (value >= 0) {
        return {
          topLeft: 10,
          topRight: 10,
        };
      } else {
        return {
          bottomLeft: 10,
          bottomRight: 10,
        };
      }
    },
    borderSkipped: false,
    barThickness(context) {
      return getLayout(context.chart).backgroundWidth;
    },
    order: 10,
  });

  // Created / Canceled pair
  REVENUE_FIELD_ORDER.created.forEach((field, index) => {
    const stackId = `pair-${index}`;

    datasets.push({
      label: REVENUE_FIELD_LABELS[field],
      type: 'bar',
      data: created[field],
      backgroundColor: (ctx) => {
        const chart = ctx.chart;
        const active = chart.getActiveElements();

        if (active.length > 0) {
          const hoverIndex = active[0].index;

          if (ctx.dataIndex === hoverIndex) {
            return CHART_COLORS.created[field].hover; // 👈 hover 高亮
          }
        }

        return CHART_COLORS.created[field].base; // 默认
      },
      hoverBackgroundColor: (ctx) => {
        const chart = ctx.chart;
        const active = chart.getActiveElements();

        if (active.length > 0) {
          const hoverIndex = active[0].index;

          if (ctx.dataIndex === hoverIndex) {
            return CHART_COLORS.created[field].hover; // 👈 hover 高亮
          }
        }

        return CHART_COLORS.created[field].base; // 默认
      },
      stack: stackId, // 和 canceled 同一对共用 stack
      borderRadius: { topLeft: 5, topRight: 5 },
      borderSkipped: false,
      categoryPercentage(context) {
        return getLayout(context.chart).categoryPercentage;
      },
      barPercentage(context) {
        return getLayout(context.chart).barPercentage;
      },
      order: 5,
    });

    const canceledField = REVENUE_FIELD_ORDER.canceled[index];
    if (canceledField) {
      datasets.push({
        label: REVENUE_FIELD_LABELS[canceledField],
        type: 'bar',
        data: canceled[canceledField],
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const active = chart.getActiveElements();

          if (active.length > 0) {
            const hoverIndex = active[0].index;

            if (ctx.dataIndex === hoverIndex) {
              return CHART_COLORS.canceled[canceledField].hover; // 👈 hover 高亮
            }
          }

          return CHART_COLORS.canceled[canceledField].base; // 默认
        },
        hoverBackgroundColor: (ctx) => {
          const chart = ctx.chart;
          const active = chart.getActiveElements();

          if (active.length > 0) {
            const hoverIndex = active[0].index;

            if (ctx.dataIndex === hoverIndex) {
              return CHART_COLORS.canceled[canceledField].hover; // 👈 hover 高亮
            }
          }

          return CHART_COLORS.canceled[canceledField].base; // 默认
        },
        stack: stackId, // 和对应 created 对齐到同一个 x
        borderRadius: { bottomLeft: 5, bottomRight: 5 },
        borderSkipped: false,
        categoryPercentage(context) {
          return getLayout(context.chart).categoryPercentage;
        },
        barPercentage(context) {
          return getLayout(context.chart).barPercentage;
        },
        order: 5,
      });
    }
  });

  // Net revenue line
  datasets.push({
    label: 'Net Revenue',
    data: net,
    type: 'line',
    borderColor: '#25B8C2',
    backgroundColor: 'transparent',
    borderWidth: 3,
    fill: false,
    tension: 0.4,
    cubicInterpolationMode: 'monotone',
    pointRadius: 5,
    pointHoverRadius: 9,
    pointBackgroundColor: '#fff',
    pointBorderColor: '#25B8C2',
    pointBorderWidth: 3,
    pointHoverBackgroundColor: '#25B8C2',
    pointHoverBorderColor: 'rgba(37, 184, 194, 0.30)',
    pointHoverBorderWidth: 3,
    order: 0,
  });

  chartInstance = new Chart(chartCanvas.value, {
    type: 'bar',
    data: { labels, datasets },
    plugins: [customXAxisLabelsPlugin],
    options: {
      layout: {
        padding: {
          bottom: 40, // 先给大一点，按效果再调
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler,
        },
      },
      scales: {
        x: {
          stacked: true,
          position: {
            y: 0,
          },
          grid: { 
            color: 'transparent',
            tickColor: '#666',
            drawTicks: true,
            offset: false,
          },
          ticks: { 
            display: false
          },
          border: {
            color: '#999',
            width: 1,
          },
          tickColor: '#666',
        },
        y: {
          stacked: true,
          min: yAxisConfig.min,
          max: yAxisConfig.max,
          ticks: {
            stepSize: yAxisConfig.stepSize,
            color: '#666',
            font: { size: 12, family: 'Inter', weight: '400' },
            callback(value) {
              if (value < 0) return `-$${Math.abs(value).toLocaleString()}`;
              return `$${value.toLocaleString()}`;
            },
          },
          grid: { 
            color: '#E0E0E0',
            tickColor: '#666',
          },
          border: {
            color: '#999',
            width: 1,
            dash: [5, 5]
          },
          tickColor: '#666',
        },
      },
    },
  });
}

watch(buckets, () => nextTick(renderChart), { deep: true });
onMounted(() => nextTick(renderChart));
onUnmounted(() => { if (chartInstance) chartInstance.destroy(); });
</script>

<style scoped>
.orders-revenue-section {
  background: #F9FBFD;
  border-radius: 10px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #333;
  margin: 0;
}

.info-box {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  background: #E8F4FD;
  border-radius: 8px;
}

.info-box-content {
  display: flex;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 3px;
}

.info-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.info-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: #333;
  margin: 0;
}

.info-message {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  margin: 0;
}

.info-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 0.2s;
}

.info-close:hover {
  opacity: 0.7;
}

.chart-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 15px 0 rgba(164, 164, 164, 0.25);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chart-wrapper {
  position: relative;
  height: 340px;
  width: 100%;
}

.chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 14px;
}

.legend-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 7.7px;
}

.legend-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-item span {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  white-space: nowrap;
}

.chart-disclaimer {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #666;
  margin: 0;
  width: 100%;
  text-align: left;
}

.chart-wrapper .custom-tooltip {
  position: absolute;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1000;
  background: transparent !important;
}
</style>

<style>
.chart-wrapper .tooltip-content {
  background: #F9FBFD;
  border-radius: 5px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.3);
  padding: 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: nowrap;
  min-width: 305px;
}

.chart-wrapper .tooltip-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: #666;
  margin: 0;
  text-align: left;
}

.chart-wrapper .tooltip-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 18px;
}

.chart-wrapper .tooltip-label-bold {
  flex: 1;
  font-weight: 700;
  color: #333;
  text-align: left;
}

.chart-wrapper .tooltip-label-bold.tooltip-clinic {
  color: #25B8C2;
}

.chart-wrapper .tooltip-label-bold.tooltip-lab {
  color: #004879;
}

.chart-wrapper .tooltip-value {
  font-weight: 300;
  color: #333;
  text-align: right;
}

.chart-wrapper .tooltip-value.tooltip-clinic {
  color: #25B8C2;
}

.chart-wrapper .tooltip-value.tooltip-lab {
  color: #004879;
}

.chart-wrapper .tooltip-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chart-wrapper .tooltip-item {
  padding-left: 0;
}

.chart-wrapper .tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chart-wrapper .tooltip-label {
  flex: 1;
  font-weight: 300;
  color: #333;
  text-align: left;
}
</style>
